import { LayoutDashboard, Database, Users, Wallet, Package, FileText, CalendarDays } from "lucide-react";
import "../css/Sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import useSubscriptionStore from "../store/builders/useSubscriptionStore";
import usePortfolioStore from "../store/builders/usePortfolioStore";
import useServiceStore from "../store/builders/useServiceStore";
import useAuthStore from "../store/useAuthStore";

const Row = ({ active, onClick, children, badge, badgeColor = "bg-red-500", warning, warningTitle }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "w-full",
      "text-left",
      "text-sm",
      "font-semibold",
      "rounded-xl",
      "transition-colors",
      "px-4 py-3",
      "flex items-center gap-3",
      "hover:bg-[#616161]",
      active ? "bg-[#616161]" : "",
      "relative"
    ].join(" ")}
  >
    {children}
    {(badge > 0 || warning) && (
      <div className="ml-auto flex items-center">
        {warning && (
          <span
            className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 border border-amber-500 text-amber-800 text-xs font-bold shadow-sm"
            title={warningTitle || "Nothing added yet — open this section to add items"}
          >
            !
          </span>
        )}
        {!warning && badge > 0 && (
          <span className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full ${badgeColor} text-white text-[10px] font-bold shadow-sm`}>
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
    )}
  </button>
);

const ImgIcon = ({ src, alt }) => (
  <img
    src={src}
    alt={alt}
    className="h-6 w-6 shrink-0 opacity-100 pointer-events-none"
  />
);

const INCHARGE_PROJECT_TAB = "master-data";

const Sidebar = ({ isOpen, setIsOpen, builderStatus, role = "builder" }) => {
  const isIncharge = role === "incharge";
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const isActive = (path) => location.pathname === path;

  const getActiveProjectId = () => {
    const match = location.pathname.match(/\/builder\/project\/(\d+)\/detail/);
    if (match) return match[1];
    return sessionStorage.getItem("incharge_active_project_id");
  };

  const isInchargeProjectTabActive = (tabId) => {
    if (!location.pathname.match(/\/builder\/project\/\d+\/detail/)) return false;
    const currentTab = new URLSearchParams(location.search).get("tab") || INCHARGE_PROJECT_TAB;
    return currentTab === tabId;
  };

  const { history, fetchSubscriptionHistory } = useSubscriptionStore();
  const { portfolio, fetchPortfolio } = usePortfolioStore();
  const { builderServices, fetchBuilderService } = useServiceStore();
  const { user } = useAuthStore();

  // Handle sidebar scroll persistence
  useEffect(() => {
    // 1. Restore scroll position on mount
    const savedScrollPos = sessionStorage.getItem("sidebar_scroll_pos");
    if (savedScrollPos && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(savedScrollPos, 10);
    }

    // 2. Save scroll position on scroll
    const handleScroll = () => {
      if (sidebarRef.current) {
        sessionStorage.setItem("sidebar_scroll_pos", sidebarRef.current.scrollTop.toString());
      }
    };

    const currentSidebar = sidebarRef.current;
    if (currentSidebar) {
      currentSidebar.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentSidebar) {
        currentSidebar.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  // Portfolio / services lengths (for empty-state hint only — not an error)
  const [counts, setCounts] = useState({
    portfolioCount: 0,
    servicesCount: 0,
  });

  // Fetch subscription + portfolio/services for sidebar hints (avoid heavy unused list APIs)
  useEffect(() => {
    const loadSidebarData = async () => {
      if (builderStatus === "Approved" || builderStatus === "Active") {
        fetchSubscriptionHistory();
      }

      if (builderStatus === "Active") {
        await Promise.all([fetchPortfolio(), fetchBuilderService()]);
      }
    };

    loadSidebarData();
  }, [builderStatus, fetchSubscriptionHistory, fetchPortfolio, fetchBuilderService]);

  // Sync Portfolio/Service counts from stores to local state for "Empty" check
  useEffect(() => {
    setCounts(prev => ({
      ...prev,
      portfolioCount: portfolio?.length || 0,
      servicesCount: builderServices?.length || 0
    }));
  }, [portfolio, builderServices]);

  // Check if builder has active subscription
  useEffect(() => {
    const activeSubscription = history.find(
      (sub) => sub.status?.toLowerCase() === 'active'
    );
    setHasActiveSubscription(!!activeSubscription);
  }, [history]);


  const handleNavigation = (path, badgeKey = null, currentTotal = 0) => {
    navigate(path);
    if (window.innerWidth < 1024) setIsOpen(false);

    // If navigation target corresponds to a notification badge, mark as seen
    if (badgeKey) {
      localStorage.setItem(`lastSeen_${badgeKey}`, currentTotal.toString());
      // Force re-render/re-calc of badge by updating specific count (trivial update or just let re-render happen naturally?)
      // Since Badge is calculated from `counts` state + localStorage, and we just updated localStorage, 
      // we need to trigger a re-render. Updating `counts` with same value triggers re-render? No.
      // We can use a dummy state or just rely on next fetch. But instant feedback is better.
      // Quick hack: Just force update counts to trigger re-render?
      setCounts(prev => ({ ...prev }));
    }
  };

  const navigateInchargeProjectTab = (tabId) => {
    const projectId = getActiveProjectId();
    if (!projectId) {
      handleNavigation("/builder/projects");
      return;
    }
    sessionStorage.setItem("incharge_active_project_id", String(projectId));
    navigate(`/builder/project/${projectId}/detail?tab=${tabId}`);
    if (window.innerWidth < 1024) setIsOpen(false);
  };

  const isApproved = builderStatus === "Approved";
  const isActiveStatus = builderStatus === "Active";

  // Builders with Rejected or On_hold status can only access Dashboard (like Pending_Verification)
  const canAccessAllTabs = isActiveStatus || (isApproved && hasActiveSubscription) || builderStatus === "Onboarded";
  const showSubscriptionOnly = isApproved && !hasActiveSubscription && !isActiveStatus;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div
        ref={sidebarRef}
        className={`sidebar h-full w-56 text-white transition-all duration-300 fixed lg:relative z-50 overflow-y-auto py-4 pb-32 ${isOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
        style={{
          backgroundColor: "#1F1E1E",
          borderRadius: "24px",
          zIndex: 9998,
          height: "100%",
        }}
      >
        {/* Logo */}
        <div className="flex items-center justify-center h-16 px-4 mb-4">
          <img
            src="/assets/Transparent background.png"
            alt="Ducktail Logo"
            onClick={() => handleNavigation("/")}
            className="h-12 pr-4 cursor-pointer"
          />
        </div>

        {/* Nav */}
        <nav className="mt-2 space-y-2">

          {!isIncharge && (
            <Row
              active={isActive("/builder/profile")}
              onClick={() => handleNavigation("/builder/profile")}
            >
              <ImgIcon src="/sidebaricons/Profile1.png" alt="Profile" />
              <span>My Profile</span>
            </Row>
          )}

          {/* Dashboard - Visible for non-incharge */}
          {!isIncharge && (
            <Row
              active={isActive(role === "consultant" ? "/consultant/dashboard" : "/builder/dashboard")}
              onClick={() => handleNavigation(role === "consultant" ? "/consultant/dashboard" : "/builder/dashboard")}
            >
              {role === "consultant" ? (
                <ImgIcon src="/sidebaricons/Profile1.png" alt="Profile" />
              ) : (
                <LayoutDashboard size={18} className="text-white pointer-events-none" />
              )}
              <span>{role === "consultant" ? "Profile" : "Dashboard"}</span>
            </Row>
          )}

          {/* Consultant-specific tabs */}
          {role === "consultant" && (
            <>
              {/* Subscription */}
              <Row
                active={isActive("/consultant/subscription")}
                onClick={() => handleNavigation("/consultant/subscription")}
              >
                <ImgIcon src="/sidebaricons/rejected1.png" alt="Subscription" />
                <span>Subscription</span>
              </Row>

              {/* Subscription History */}
              <Row
                active={isActive("/consultant/subscription-history")}
                onClick={() => handleNavigation("/consultant/subscription-history")}
              >
                <ImgIcon src="/sidebaricons/rejected1.png" alt="Subscription History" />
                <span>Subscription History</span>
              </Row>
            </>
          )}

          {/* Incharge Role: Restricted Sidebar */}
          {isIncharge && (
            <>
              {/* Projects */}
              <Row
                active={location.pathname.startsWith("/builder/projects")}
                onClick={() => handleNavigation("/builder/projects")}
              >
                <ImgIcon src="/sidebaricons/Job list3.png" alt="Projects" />
                <span>My Projects</span>
              </Row>
              {/* Master Data */}
              <Row
                active={isInchargeProjectTabActive("master-data")}
                onClick={() => navigateInchargeProjectTab("master-data")}
              >
                <Database className="w-5 h-5 text-gray-300" />
                <span>Master Data</span>
              </Row>
              {/* Attendance */}
              <Row
                active={isInchargeProjectTabActive("attendance")}
                onClick={() => navigateInchargeProjectTab("attendance")}
              >
                <Users className="w-5 h-5 text-gray-300" />
                <span>Attendance</span>
              </Row>
              {/* Weekly Update */}
              <Row
                active={isInchargeProjectTabActive("weekly-update")}
                onClick={() => navigateInchargeProjectTab("weekly-update")}
              >
                <CalendarDays className="w-5 h-5 text-gray-300" />
                <span>Weekly Update</span>
              </Row>
              {/* Petty Cash */}
              <Row
                active={isInchargeProjectTabActive("pettycash")}
                onClick={() => navigateInchargeProjectTab("pettycash")}
              >
                <Wallet className="w-5 h-5 text-gray-300" />
                <span>Petty Cash</span>
              </Row>
              {/* Materials */}
              <Row
                active={isInchargeProjectTabActive("materials")}
                onClick={() => navigateInchargeProjectTab("materials")}
              >
                <Package className="w-5 h-5 text-gray-300" />
                <span>Materials</span>
              </Row>
              {/* Docs */}
              <Row
                active={isInchargeProjectTabActive("docs")}
                onClick={() => navigateInchargeProjectTab("docs")}
              >
                <FileText className="w-5 h-5 text-gray-300" />
                <span>Docs</span>
              </Row>
            </>
          )}

          {/* All other tabs - Visible when approved with active subscription */}
          {role === "builder" && canAccessAllTabs && (
            <>
              {/* 2. Project Enquiry */}
              <Row
                active={isActive("/builder/projectenquiry")}
                onClick={() => handleNavigation("/builder/projectenquiry")}
              >
                <ImgIcon src="/sidebaricons/project invite 1.png" alt="Project Enquiry" />
                <span>Project Enquiry</span>
              </Row>

              {/* 3. Project Invite */}
              <Row
                active={isActive("/builder/projectinvite")}
                onClick={() => handleNavigation("/builder/projectinvite")}
              >
                <ImgIcon src="/sidebaricons/project invite 1.png" alt="Project Invite" />
                <span>Project Invite</span>
              </Row>


           

              {/* 6. Projects (Unified) */}
              <Row
                active={location.pathname.startsWith("/builder/projects")}
                onClick={() => handleNavigation("/builder/projects")}
              >
                <ImgIcon src="/sidebaricons/Job list3.png" alt="Projects" />
                <span>Projects</span>
              </Row>

              {/* 16. ERP */}
              <Row
                active={location.pathname.startsWith("/builder/erp")}
                onClick={() => handleNavigation("/builder/erp")}
              >
                <ImgIcon src="/sidebaricons/Job list3.png" alt="ERP" />
                <span>ERP</span>
              </Row>

          


              {/* 11b. Consultant Management (Unified) - Hidden via comments */}
              {/* <Row
                active={location.pathname === "/builder/consultant"}
                onClick={() => handleNavigation("/builder/consultant")}
              >
                <ImgIcon src="/sidebaricons/Profile1.png" alt="Consultant" />
                <span>Consultant</span>
              </Row> */}

              {/* 12. Jobs */}
              {/* <Row
                active={isActive("/builder/jobs")}
                onClick={() => handleNavigation("/builder/jobs")}
              >
                <ImgIcon src="/sidebaricons/Job list3.png" alt="Jobs" />
                <span>Jobs</span>
              </Row> */}



              {/* 13. Subscription */}
              <Row
                active={isActive("/builder/subscription")}
                onClick={() => handleNavigation("/builder/subscription")}
              >
                <ImgIcon src="/sidebaricons/rejected1.png" alt="Subscription" />
                <span>Subscription</span>
              </Row>


           
              {/* 15. Real Estate (Hidden) */}
              {/* <Row
                active={isActive("/builder/real-estate")}
                onClick={() => handleNavigation("/builder/real-estate")}
              >
                <ImgIcon src="/sidebaricons/Profile 2.png" alt="Real Estate" />
                <span>Real Estate</span>
              </Row> */}

              {/* 16. Plot */}
              <Row
                active={location.pathname.startsWith("/builder/plot")}
                onClick={() => handleNavigation("/builder/plot")}
              >
                <ImgIcon src="/sidebaricons/Profile 2.png" alt="Plot" />
                <span>Plot</span>
              </Row>
                    {/* 11. Profile */}
             
            

              {/* 17. Customer Enquiry */}

            </>
          )}

          {/* Subscription & History - Visible for Approved status without active subscription */}
          {showSubscriptionOnly && (
            <>
              {/* Subscription */}
              <Row
                active={isActive("/builder/subscription")}
                onClick={() => handleNavigation("/builder/subscription")}
              >
                <ImgIcon src="/sidebaricons/rejected1.png" alt="Subscription" />
                <span>Subscription</span>
              </Row>

            </>
          )}
        </nav>
      </div>
    </>
  );
};

export default Sidebar;